import React from 'react';
import { useNavigate } from 'react-router-dom';
import { expertise, credentials } from '../mock';
import { Check, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';

export const About = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#1e1919' }}>
              About Re-Surge Device Solutions LTD
            </h1>
            <p className="text-xl" style={{ color: '#736c64' }}>
              Execution-led business rebuilding for founders and operators in tech resale, wholesale, and marketplace operations.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials Strip */}
      <section className="py-12 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {credentials.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold mb-2" style={{ color: '#059669' }}>
                  {item.stat}
                </div>
                <div className="text-sm" style={{ color: '#736c64' }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e1919' }}>
                The Company
              </h2>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Re-Surge Device Solutions LTD provides execution-led business rebuilding for founders and operators who need real operational expertise. We specialize in circular economy, ITAD (IT Asset Disposition), tech resale, wholesale, marketplace strategy, and scaling without chaos.
              </p>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                The company has done business of €50m+ across the last two years. This is not theory. This is proven execution, tested systems, and knowing where money leaks before it happens.
              </p>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e1919' }}>
                The Founder
              </h2>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Sean Gavin founded Re-Surge after 13+ years in the industry. His career progression tells the story: shop floor to technician to sales manager to top operator. He has seen every part of the business from the inside.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e1919' }}>
                Market Position
              </h2>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Strategic partner with Refurbed and former top-tier Back Market seller. Left Back Market due to platform fraud issues and commission misbehaviours. This was a calculated decision after seeing systemic problems that were not being addressed.
              </p>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Direct relationships with telcos across multiple markets. Wholesale networks spanning EU, USA, Dubai, Singapore, China, Japan, and Australia. Software expertise with multi-channel platforms for marketplace operations.
              </p>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Strong logistics partnerships with DHL and UPS. Not just accounts. Negotiated rates, insurance solutions, and priority handling during peak seasons.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e1919' }}>
                What This Means
              </h2>
              <p className="text-lg mb-4" style={{ color: '#736c64' }}>
                Re-Surge exists because Sean repeatedly saw the same failures in other businesses. Poor execution. Weak systems. Marketplace strategy built on hope. Cashflow blindness. Fraud and operational leakage eating profits.
              </p>
              <p className="text-lg font-semibold" style={{ color: '#059669' }}>
                This is operator-level work. Not coaching. Not advice from slides. Direct intervention to fix what is actually broken.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Experience */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#1e1919' }}>
            Direct Experience
          </h2>
          <div className="bg-white p-8 rounded-lg border border-gray-200">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {expertise.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#059669' }} />
                  <span style={{ color: '#736c64' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Career Timeline */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#1e1919' }}>
            Career Progression
          </h2>
          <div className="space-y-6">
            <div className="border-l-2 pl-6" style={{ borderColor: '#059669' }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e1919' }}>Shop Floor</h3>
              <p style={{ color: '#736c64' }}>Started at ground level. Learned how devices actually move, how customers actually behave, and where operations actually break.</p>
            </div>
            <div className="border-l-2 pl-6" style={{ borderColor: '#059669' }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e1919' }}>Technician</h3>
              <p style={{ color: '#736c64' }}>Hands-on device repair and quality control. Understanding grading, warranties, and what actually causes returns.</p>
            </div>
            <div className="border-l-2 pl-6" style={{ borderColor: '#059669' }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e1919' }}>Sales Manager</h3>
              <p style={{ color: '#736c64' }}>Managing B2B relationships, marketplace strategy, and team performance. Scaling revenue while maintaining margins.</p>
            </div>
            <div className="border-l-2 pl-6" style={{ borderColor: '#059669' }}>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1e1919' }}>Top Operator</h3>
              <p style={{ color: '#736c64' }}>Running full operations. €50m+ business volume across last two years. Thousands of devices weekly. Managing fraud, refunds, warranties, cashflow, and scale.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Connect */}
      <section className="py-20" style={{ backgroundColor: '#1e1919' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">
            Connect
          </h2>
          <p className="text-xl mb-8 text-gray-400">
            Ready to fix what is broken in your business?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToContact}
              size="lg"
              className="text-lg px-8"
              style={{ backgroundColor: '#059669', color: 'white' }}
            >
              Work With Us
            </Button>
            <Button
              onClick={() => window.open('https://www.linkedin.com/in/sean-gavin-0b1952a6/', '_blank')}
              size="lg"
              variant="outline"
              className="text-lg px-8 border-gray-600 text-white hover:bg-gray-800"
            >
              <Linkedin className="h-5 w-5 mr-2" />
              LinkedIn
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};